import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormHelperText,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { FormikCodeField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton';

import { useCompleteShareAuthMutation } from 'generated/shared_resource.generated';

interface VerifyCodeProps {
  share_id: string;
  email: string;
  onComplete: (token: string) => void;
  onReset: () => void;
}

interface FormValues {
  code: string[];
  submit: string | null;
}

const messages = defineMessages({
  subtitle: {
    id: 'sharedResource.verifyCode.subtitle',
    defaultMessage:
      'Verify your email address. We sent a 6-digit code to your email.',
  },
  header: {
    id: 'sharedResource.verifyCode.header',
    defaultMessage: 'Verification Code',
  },
  submitText: {
    id: 'sharedResource.verifyCode.submitText',
    defaultMessage: 'View Record',
  },
  incorrectCode: {
    id: 'sharedResource.verifyCode.incorrectCode',
    defaultMessage: 'Incorrect code. The share link may have expired.',
  },
  noCode: {
    id: 'sharedResource.verifyCode.noCode',
    defaultMessage: "Didn't receive a code?",
  },
  tryAgain: {
    id: 'sharedResource.verifyCode.tryAgain',
    defaultMessage: 'Try Again',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'Required',
  },
});

function VerifyCode({ share_id, email, onComplete, onReset }: VerifyCodeProps) {
  const { formatMessage } = useIntl();
  const [completeShareAuth] = useCompleteShareAuthMutation();

  const handleSubmit = async (
    values: FormValues,
    formikHelpers: FormikHelpers<FormValues>
  ) => {
    const code = values.code.join('');
    const response = await completeShareAuth({
      variables: {
        input: { code, share_id, email: email.toLowerCase() },
      },
    });
    if (response.errors) {
      formikHelpers.setErrors({
        submit: formatMessage(messages.incorrectCode),
      });
      return;
    }
    const token = response.data?.completeShareAuth.token;
    if (token) {
      onComplete(token);
    }
  };

  const handleReset = () => {
    onReset();
  };

  const validationSchema: Yup.Schema<Partial<FormValues>> = Yup.object().shape({
    code: Yup.array()
      .of(Yup.string().required(formatMessage(messages.required)))
      .required(),
  });

  const initialValues: FormValues = {
    code: ['', '', '', '', '', ''],
    submit: null,
  };

  return (
    <Stack spacing={3}>
      <Typography color="primary.contrastText" variant="h5">
        {formatMessage(messages.subtitle)}
      </Typography>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, errors }) => (
          <Form noValidate>
            <Card sx={{ maxWidth: 500 }}>
              <CardHeader title={formatMessage(messages.header)} />
              <Divider />
              <CardContent>
                <FormikCodeField name="code" codeLength={6} />
                {errors.submit && (
                  <FormHelperText error>{errors.submit}</FormHelperText>
                )}
              </CardContent>
              <Divider />
              <CardActions>
                <RoundedButton disabled={isSubmitting} type="submit">
                  {formatMessage(messages.submitText)}
                </RoundedButton>
              </CardActions>
            </Card>
            <Stack alignItems="center" direction="row" spacing={1}>
              <Typography color="primary.contrastText">
                {formatMessage(messages.noCode)}
              </Typography>
              <Link color="primary.contrastText" onClick={handleReset}>
                {formatMessage(messages.tryAgain)}
              </Link>
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
}

export default VerifyCode;
